import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'
import { UserRole } from '@lmc/ts-common'
import {
  EmptyState,
  ExpandableCard,
  type ExpandableCardField,
  Heading,
  NavigationModal,
  Spinner
} from '@lmc/ui-kit'
import { type MenuProps, message } from 'antd'
import { NAMESPACE } from 'locale'
import { serviceRecordConstants } from 'locale/serviceRecords'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import useRoleAccess from '../../../../../components/AccessControl/useRoleAccess'
import { useInfiniteScrollFetch } from '../../../../../hooks/useInfiniteScroll'
import { useServiceRecordArchive } from '../hooks/useServiceRecordArchive'
import { useServiceRecordDocumentRemove } from '../hooks/useServiceRecordDocumentRemove'
import type { ServiceRecord } from '../types'
import { formatDate, formatDateTime } from '../utils/serviceRecordUtils'

interface ServiceRecordListProps {
  serviceRecords: ServiceRecord[]
  isLoading: boolean
  onEdit: (serviceRecord: ServiceRecord) => void
  onRefetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const STATUS_TAG_COLORS: Record<string, string> = {
  active: 'green',
  archived: 'default'
}

export const ServiceRecordList: React.FC<ServiceRecordListProps> = ({
  serviceRecords,
  isLoading,
  onEdit,
  onRefetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_RECORDS)
  const { id: residentId } = useParams<{ id: string }>()
  const [expandedServiceRecordId, setExpandedServiceRecordId] = useState<
    string | null
  >(null)
  const [archiveModalVisible, setArchiveModalVisible] = useState(false)
  const [selectedServiceRecord, setSelectedServiceRecord] =
    useState<ServiceRecord | null>(null)
  const [showRemoveDocModal, setShowRemoveDocModal] = useState(false)
  const [docToRemove, setDocToRemove] = useState<{
    recordId: string
    documentId: string
    filename: string
  } | null>(null)

  const { archiveServiceRecord, unarchiveServiceRecord } =
    useServiceRecordArchive(residentId || '', () => {
      setArchiveModalVisible(false)
      setSelectedServiceRecord(null)
    })
  const { removeDocument } = useServiceRecordDocumentRemove({
    residentId: residentId || ''
  })

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  const hasEditAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'ServiceRecord'
      }
    ]
  )

  const hasArchiveAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'ServiceRecord'
      }
    ]
  )

  if (isLoading) {
    return <Spinner />
  }

  if (serviceRecords.length === 0) {
    return (
      <EmptyState
        title={t(serviceRecordConstants.NO_SERVICE_RECORDS_FOUND)}
        description={t(serviceRecordConstants.NO_SERVICE_RECORDS_DESCRIPTION)}
      />
    )
  }

  const activeServiceRecords = serviceRecords.filter(
    (record) => !record.archivedAt
  )
  const archivedServiceRecords = serviceRecords.filter((record) =>
    Boolean(record.archivedAt)
  )

  const toggleExpand = (serviceRecordId: string) => {
    setExpandedServiceRecordId(
      expandedServiceRecordId === serviceRecordId ? null : serviceRecordId
    )
  }

  const handleArchiveClick = (serviceRecord: ServiceRecord) => {
    setSelectedServiceRecord(serviceRecord)
    setArchiveModalVisible(true)
  }

  const handleConfirmArchive = async () => {
    if (!selectedServiceRecord) return

    try {
      if (selectedServiceRecord.archivedAt) {
        await unarchiveServiceRecord(selectedServiceRecord.id)
      } else {
        await archiveServiceRecord(selectedServiceRecord.id)
      }
    } catch (error) {
      console.error('Failed to archive/unarchive service record:', error)
    }
  }

  const getMenuItems = (serviceRecord: ServiceRecord): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (hasEditAccess && !serviceRecord.archivedAt) {
      items.push({
        key: 'edit',
        label: t(serviceRecordConstants.EDIT),
        onClick: () => onEdit(serviceRecord)
      })
    }

    if (hasArchiveAccess) {
      items.push({
        key: 'archive',
        label: serviceRecord.archivedAt
          ? t(serviceRecordConstants.UN_ARCHIVE)
          : t(serviceRecordConstants.ARCHIVE),
        onClick: () => handleArchiveClick(serviceRecord)
      })
    }

    return items
  }

  const getStatusLabel = (serviceRecord: ServiceRecord) => {
    return serviceRecord.archivedAt
      ? t(serviceRecordConstants.ARCHIVED)
      : t(serviceRecordConstants.ACTIVE)
  }

  const getActorName = (actor?: ServiceRecord['createdBy']) => {
    if (!actor) return ''

    const first = actor.name?.first || ''
    const last = actor.name?.last || ''
    const fullName = `${first} ${last}`.trim()

    return fullName || actor._id
  }

  const buildFields = (serviceRecord: ServiceRecord): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []

    if (serviceRecord.description) {
      fields.push({
        label: t(serviceRecordConstants.DESCRIPTION_OF_SERVICE_RECORD),
        value: serviceRecord.description,
        fullWidth: true
      })
    }

    if (serviceRecord.providerAddress) {
      fields.push({
        label: 'Provider address',
        value: serviceRecord.providerAddress,
        fullWidth: true
      })
    }

    if (serviceRecord.documents && serviceRecord.documents.length > 0) {
      const documentLinks = serviceRecord.documents.map((doc, index) => {
        const filename = doc.filename || `Document ${index + 1}`
        const url = doc.url

        if (!url) {
          return <span key={index}>{filename}</span>
        }

        const docId = doc.id

        return (
          <DocumentRow key={index}>
            <DocumentLink href={url} target="_blank" rel="noopener noreferrer">
              {filename}
            </DocumentLink>
            {hasEditAccess && !serviceRecord.archivedAt && docId && (
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation()
                  setDocToRemove({
                    recordId: serviceRecord.id,
                    documentId: docId,
                    filename
                  })
                  setShowRemoveDocModal(true)
                }}
                title="Remove document"
              >
                <CloseOutlined />
              </RemoveButton>
            )}
          </DocumentRow>
        )
      })

      fields.push({
        label: 'Documents',
        value: <DocumentLinksContainer>{documentLinks}</DocumentLinksContainer>,
        fullWidth: true
      })
    }

    if (serviceRecord.createdBy) {
      const createdByName = getActorName(serviceRecord.createdBy)
      fields.push({
        label: t(serviceRecordConstants.CREATED_BY),
        value: `${createdByName}${
          serviceRecord.createdAt
            ? ` on ${formatDateTime(serviceRecord.createdAt)}`
            : ''
        }`
      })
    }

    if (serviceRecord.updatedBy) {
      const updatedByName = getActorName(serviceRecord.updatedBy)
      fields.push({
        label: t(serviceRecordConstants.UPDATED_BY),
        value: `${updatedByName}${
          serviceRecord.updatedAt
            ? ` on ${formatDateTime(serviceRecord.updatedAt)}`
            : ''
        }`
      })
    }

    return fields
  }

  const renderServiceRecord = (serviceRecord: ServiceRecord) => {
    const dateText = formatDate(serviceRecord.startDate)
    const menuItems = getMenuItems(serviceRecord)
    const fields = buildFields(serviceRecord)
    const statusTagColor = serviceRecord.archivedAt
      ? STATUS_TAG_COLORS.archived
      : STATUS_TAG_COLORS.active

    return (
      <ExpandableCard
        key={serviceRecord.id}
        id={serviceRecord.id}
        title={serviceRecord.provider || 'Service record'}
        status={
          serviceRecord.archivedAt
            ? {
                label: getStatusLabel(serviceRecord),
                color: statusTagColor
              }
            : undefined
        }
        date={
          dateText
            ? `Service record: ${dateText}`
            : t(serviceRecordConstants.NO_DATE_TIME_SPECIFIED)
        }
        titleIndicators={
          serviceRecord.documents && serviceRecord.documents.length > 0 ? (
            <AttachmentIcon title="Has attachment">
              <PaperClipOutlined />
              {serviceRecord.documents.length > 1 && (
                <span style={{ marginLeft: '4px' }}>
                  ({serviceRecord.documents.length})
                </span>
              )}
            </AttachmentIcon>
          ) : undefined
        }
        fields={fields}
        menuItems={menuItems}
        isExpanded={expandedServiceRecordId === serviceRecord.id}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`service-record-${serviceRecord.id}`}
      />
    )
  }

  return (
    <>
      <ScrollContainer ref={rootRef}>
        <ListContainer>
          {activeServiceRecords.map(renderServiceRecord)}

          {archivedServiceRecords.length > 0 && (
            <>
              <SectionHeading>
                <Heading level={4}>
                  {t(serviceRecordConstants.ARCHIVED_SERVICE_RECORDS)}
                </Heading>
              </SectionHeading>
              {archivedServiceRecords.map(renderServiceRecord)}
            </>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <LoadMoreContainer ref={infiniteRef}>
              {isFetchingNextPage && <Spinner />}
            </LoadMoreContainer>
          )}
        </ListContainer>
      </ScrollContainer>

      <NavigationModal
        showPrompt={archiveModalVisible}
        confirmNavigation={handleConfirmArchive}
        cancelNavigation={() => {
          setArchiveModalVisible(false)
          setSelectedServiceRecord(null)
        }}
        modalTitle={
          selectedServiceRecord?.archivedAt
            ? t(serviceRecordConstants.RESTORE_SERVICE_RECORD)
            : t(serviceRecordConstants.ARCHIVE_SERVICE_RECORD)
        }
        modalContentText={
          selectedServiceRecord?.archivedAt
            ? t(serviceRecordConstants.RESTORE_CONFIRMATION)
            : t(serviceRecordConstants.ARCHIVE_CONFIRMATION)
        }
        modalConfirmText={
          selectedServiceRecord?.archivedAt
            ? t(serviceRecordConstants.RESTORE)
            : t(serviceRecordConstants.ARCHIVE)
        }
        modalCancelText={t(serviceRecordConstants.CANCEL)}
      />
      <NavigationModal
        showPrompt={showRemoveDocModal}
        modalTitle="Remove Document"
        modalContentText={`Are you sure you want to remove "${docToRemove?.filename}"? This action cannot be undone.`}
        modalConfirmText="Remove"
        modalCancelText="Cancel"
        confirmNavigation={async () => {
          if (!docToRemove) return

          const latestRecord = serviceRecords.find(
            (record) => record.id === docToRemove.recordId
          )
          const latestDocumentIds =
            latestRecord?.documents?.map((document) => document.id) || []

          if (!latestDocumentIds.includes(docToRemove.documentId)) {
            message.warning('Document is no longer available')
            setShowRemoveDocModal(false)
            setDocToRemove(null)
            onRefetch()
            return
          }

          const updatedDocumentIds = latestDocumentIds.filter(
            (docId) => docId !== docToRemove.documentId
          )

          try {
            await removeDocument(docToRemove.recordId, updatedDocumentIds)
            setShowRemoveDocModal(false)
            setDocToRemove(null)
          } catch (error) {
            console.error('Failed to remove document:', error)
          }
        }}
        cancelNavigation={() => {
          setShowRemoveDocModal(false)
          setDocToRemove(null)
        }}
      />
    </>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const SectionHeading = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`

const AttachmentIcon = styled.span`
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  font-size: 14px;
`

const DocumentLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DocumentLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.color?.status?.danger || '#ff4d4f'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
